/**
 * `sailfish-autofix fork <issue-id>` — Download an autofix session log and resume it locally.
 *
 * Looks up the latest autofix run for the given issue, downloads its CC session
 * log from GCS, and (by default) writes it into ~/.claude/projects/{encoded-git-root}/
 * so `claude --resume <sessionId>` works. `--agent <agent>` instead resumes the
 * session in a chosen coding agent (codex / cursor / opencode / gemini / goose /
 * windsurf), converting the transcript to that agent's resume format.
 *
 * Either way, the resumed session opens with a one-time worktree/branch
 * "orientation" that offers the user how to work (fresh worktree / in place /
 * just discuss) before any code changes.
 */

import { execSync, spawn, spawnSync } from "child_process";
import { randomUUID } from "crypto";
import { mkdirSync, writeFileSync, unlinkSync } from "fs";
import { homedir, tmpdir } from "os";
import { join, resolve } from "path";
import { readCredentials, isExpired, getCredentialsPath } from "../auth/credentials.js";
import { exchangeToken } from "../auth/oauth.js";
import { AutofixBackendClient } from "../mcp-server/autofix-backend-client.js";
import {
  DOWNLOAD_ARTIFACT,
  GET_LATEST_AUTOFIX_RUN_BY_ISSUE_ID,
} from "../mcp-server/autofix-graphql-queries.js";
import { extractPortableSession } from "../resume/extractor.js";
import { renderNonNative } from "../resume/render.js";
import { renderOpenCode } from "../resume/emit-opencode.js";
import { prepareGooseImport } from "../resume/emit-goose.js";
import { renderGeminiCheckpoint, placeGeminiCheckpoint, geminiResumeTag } from "../resume/emit-gemini.js";

interface DownloadResult {
  downloadArtifact: {
    data: string;
    branch: string;
    artifactId: string;
    artifactType: string;
    gcsPath: string;
    size: number;
  } | null;
}

interface LatestRunResult {
  getLatestAutofixRunByIssueId: {
    runId: string;
    branchUrl: string | null;
    prUrl: string | null;
    state: string | null;
  } | null;
}

function getGitRoot(cwd?: string): string {
  try {
    return execSync("git rev-parse --show-toplevel", {
      cwd: cwd || process.cwd(),
      encoding: "utf-8",
      stdio: ["pipe", "pipe", "pipe"],
    }).trim();
  } catch {
    throw new Error(
      "Not inside a git repository. Run this from your repo, or use --cwd <path>."
    );
  }
}

function cwdToProjectDirName(cwd: string): string {
  // Must match Claude Code's own project-dir encoding, which replaces "/",
  // "_", and "." with "-". Replacing only "/" sends the session log to a
  // sibling dir (e.g. repo_2 -> repo_2 instead of repo-2), so `claude
  // --resume` can't find it for any path containing "_" or ".".
  return cwd.replace(/[/_.]/g, "-");
}

function sanitizeSessionId(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_-]/g, "_");
}

function extractSessionId(jsonlContent: string): string | null {
  for (const line of jsonlContent.split("\n")) {
    if (!line.trim()) continue;
    try {
      const parsed = JSON.parse(line);
      if (parsed.type === "system" && parsed.sessionId) return parsed.sessionId;
      if (parsed.sessionId) return parsed.sessionId;
    } catch {
      continue;
    }
  }
  return null;
}

// CWD patching: the uploaded artifact is a native CC session log, whose lines
// carry cwd fields. patchCwd rewrites them to the local git root so resume
// works regardless of where the autofix pod ran.
function extractCwd(jsonlContent: string): string | null {
  for (const line of jsonlContent.split("\n")) {
    if (!line.trim()) continue;
    try {
      const parsed = JSON.parse(line);
      if (parsed.cwd) return parsed.cwd;
    } catch {
      continue;
    }
  }
  return null;
}

function patchCwd(jsonlContent: string, originalCwd: string, newCwd: string): string {
  // Bare prefix replace so sub-paths (…/lightfield/apps/foo.ts), not just the
  // standalone cwd field, get rewritten to the local root. Safe inside JSONL:
  // filesystem paths sit in quoted strings and need no JSON escaping. Function
  // replacer so a local checkout path containing `$` (e.g. a `$1` dir) is inserted
  // literally, not treated as a `$n`/`$&` replacement pattern.
  return originalCwd ? jsonlContent.replaceAll(originalCwd, () => newCwd) : jsonlContent;
}

// The run's fix branch isn't a first-class field — it's embedded in branchUrl as
// the "/tree/<branch>" suffix (populated only after a PR exists; GitHub/GitLab
// only). Everything after "/tree/" is the branch, so a branch that itself
// contains slashes (sailfish-autofix/issue-123-…) parses correctly. Returns null
// when there is no pushed branch yet.
export function parseBranchFromUrl(branchUrl: string | null | undefined): string | null {
  if (!branchUrl) return null;
  const marker = "/tree/";
  const idx = branchUrl.indexOf(marker);
  if (idx === -1) return null;
  return branchUrl.slice(idx + marker.length).trim() || null;
}

export function parsePrNumber(prUrl: string | null | undefined): string | null {
  if (!prUrl) return null;
  const m = prUrl.match(/\/(?:pull|pull-requests|merge_requests)\/(\d+)/);
  return m ? m[1] : null;
}

function getCurrentBranch(cwd: string): string | null {
  try {
    return (
      execSync("git rev-parse --abbrev-ref HEAD", {
        cwd,
        encoding: "utf-8",
        stdio: ["pipe", "pipe", "pipe"],
      }).trim() || null
    );
  } catch {
    return null;
  }
}

// A one-time orientation the resumed agent reads on a forked session. The prompt
// is INTENTIONALLY visible: the resumed session interviews the user (worktree /
// in-place / discuss) because we don't know how they want to work. No hidden
// injection — see DIAGPLAT fork/continue decision. Reaches every --agent target
// (see runFork): claude-code/goose as a trailing turn, the rest as a prominent
// resume-package section.
export function buildOrientation(opts: {
  issueId: number;
  fixBranch: string | null;
  prUrl: string | null;
  state: string | null;
  currentBranch: string | null;
}): string {
  const { issueId, fixBranch, prUrl, state, currentBranch } = opts;
  const head = `[sailfish-autofix] I've reopened the autofix for issue ${issueId}.`;

  const prNumber = parsePrNumber(prUrl);
  const prRef = prNumber ? `PR #${prNumber}${prUrl ? ` — ${prUrl}` : ""}` : prUrl ?? "";
  const prPart = prRef ? ` (${prRef})` : "";

  if (!fixBranch) {
    // A PR exists — so a branch WAS pushed — but we can't resolve its name here
    // (e.g. Bitbucket, for which the backend stores no branch_url). Don't falsely
    // claim there's no branch.
    if (prRef) {
      return (
        `${head} A fix was made for this issue${prPart}. I can't auto-detect its branch name from here — grab ` +
        `it from the PR and I'll set up a worktree, or we can just talk through the previous run.`
      );
    }
    // No branch and no PR: nothing to check out, so don't belabour the absence —
    // a neutral greeting that confirms the resume and invites direction.
    return (
      `${head} Want me to pick up where it left off, or is there something else you'd like to look into?`
    );
  }

  const here = currentBranch ? `\`${currentBranch}\`` : "another branch";
  const s = (state ?? "").toLowerCase();

  // The fix is on a branch; how it relates to the user's tree depends on where
  // the PR ended up. A merged fix is likely already on main (so working in place
  // is the better default); an open or rejected one lives only on the branch.
  let status: string;
  let optA: string;
  let optB: string;
  if (s === "pr_merged") {
    status =
      `The fix was merged${prPart} — it's most likely already on \`main\`, so your current branch (${here}) ` +
      `may or may not have it yet.`;
    optA = `Check the original branch out in a worktree — handy if you just want to see the change as it landed.`;
    optB = `Work in your current tree (recommended) — pull the latest \`main\` and you'll have the fix.`;
  } else if (s === "pr_rejected") {
    status =
      `The fix${prPart} was closed without merging, so it never landed — but the branch is still there.`;
    optA = `Check the branch out in a fresh worktree (recommended) — pick the change back up without touching your current work.`;
    optB = `Work in your current tree.`;
  } else {
    status =
      `The fix is on branch \`${fixBranch}\`${prPart}, but your current tree is on ${here} and doesn't have ` +
      `those changes yet.`;
    optA = `Check the fix out in a fresh worktree (recommended) — no stash, your current work stays untouched.`;
    optB = `Work in your current tree.`;
  }

  return (
    `${head} ${status}\n\n` +
    `How do you want to work?\n` +
    `  (a) ${optA}\n` +
    `  (b) ${optB}\n` +
    `  (c) Just talk through the previous run — no code changes.\n\n` +
    `Tell me which — and if it's the worktree, a directory name to use. I'll set it up and remove it when ` +
    `we're done; I won't switch branches or stash without your say-so.`
  );
}

// Append the orientation as a trailing ASSISTANT turn — the interview question,
// already "asked". Sessions normally end on an assistant turn, so on resume CC
// renders it and waits for the user's answer; a trailing *user* turn instead
// just sits there unanswered (CC doesn't auto-run the model on resume). We clone
// the envelope of the last real entry (cwd/sessionId/version/gitBranch) and the
// model/usage from the last assistant entry so the line matches CC's transcript
// schema and doesn't break resume; only a fresh uuid, the parentUuid link, and
// the message body are new. stop_reason "end_turn" marks it complete so CC won't
// try to continue it. If no anchor entry is found we skip rather than risk
// writing a malformed transcript. Used for the claude-code and goose targets,
// which both consume this raw CC JSONL.
export function appendOrientationTurn(
  jsonlContent: string,
  orientation: string,
  gitRoot: string
): string {
  let anchor: any = null; // last main-chain entry with a uuid — parentUuid + envelope
  let assistant: any = null; // last main-chain assistant entry — model/usage metadata
  const lines = jsonlContent.split("\n");
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line) continue;
    let parsed: any;
    try {
      parsed = JSON.parse(line);
    } catch {
      continue;
    }
    // Skip sidechain (sub-agent) entries — anchoring there would bury the real
    // conversation leaf and the orientation wouldn't render on the main chain.
    if (parsed?.isSidechain === true) continue;
    if (!anchor && parsed?.uuid) anchor = parsed;
    if (!assistant && parsed?.type === "assistant" && parsed?.message) assistant = parsed;
    if (anchor && assistant) break;
  }
  if (!anchor) return jsonlContent;

  // Don't append after an unresolved tool call. A run SIGKILL'd mid-tool by the
  // timeout leaves a trailing assistant `tool_use` with no `tool_result`; CC
  // repairs that at the leaf, but burying it under an appended turn would send a
  // dangling `tool_use` to the API → 400. Skip the interview for that rare case
  // (the session still resumes). A clean assistant tail has stop_reason
  // end_turn/stop_sequence/max_tokens; a user tail (incl. tool_result) is safe.
  if (
    anchor.type === "assistant" &&
    anchor.message?.stop_reason !== "end_turn" &&
    anchor.message?.stop_reason !== "stop_sequence" &&
    anchor.message?.stop_reason !== "max_tokens"
  ) {
    return jsonlContent;
  }

  const turn: Record<string, unknown> = {
    parentUuid: anchor.uuid,
    isSidechain: false,
    userType: "external",
    cwd: gitRoot,
    sessionId: anchor.sessionId,
    version: anchor.version,
    gitBranch: anchor.gitBranch ?? "",
    type: "assistant",
    uuid: randomUUID(),
    requestId: `sailfish-fork-${randomUUID().slice(0, 8)}`,
    timestamp: new Date().toISOString(),
    message: {
      role: "assistant",
      type: "message",
      id: `msg_sailfish_fork_${randomUUID().replace(/-/g, "").slice(0, 20)}`,
      model: assistant?.message?.model ?? "claude-opus-4-8",
      content: [{ type: "text", text: orientation }],
      stop_reason: "end_turn",
      stop_sequence: null,
      usage: assistant?.message?.usage ?? { input_tokens: 0, output_tokens: 0 },
    },
  };

  const base = jsonlContent.endsWith("\n") ? jsonlContent : jsonlContent + "\n";
  return base + JSON.stringify(turn) + "\n";
}

// --- resume targets -------------------------------------------------------
// `fork --agent <agent>` resumes the autofix session in a chosen agent. Every
// agent is a peer target:
//   claude-code           — identity path: the session log IS a CC transcript.
//   codex / cursor        — convert to a portable RESUME.md + resume.json package.
//   opencode/gemini/goose — NATIVE resume: rebuild the session inside the target
//                           agent's own store so the model "knows" the prior work
//                           (opencode import JSON, gemini chat checkpoint, goose
//                           CC-import). See ../resume/emit-*.ts.
//   windsurf (Devin)      — non-native RESUME.md package, but MANUAL: Devin Desktop
//                           is a GUI IDE with no headless resume, so we write the
//                           package and print @-mention steps rather than spawning.
const TARGET_ALIASES: Record<string, string> = {
  "": "claude-code", // no --agent → default to claude-code (unchanged behavior)
  cc: "claude-code",
  claude: "claude-code",
  "claude-code": "claude-code",
  "cursor-agent": "cursor",
  oc: "opencode",
  "gemini-cli": "gemini",
  devin: "windsurf", // Windsurf is now Devin Desktop
};
// Recognized agents we don't convert to yet. None right now.
const PLANNED_TARGETS: string[] = [];

export function normalizeTarget(to?: string): string {
  const raw = (to ?? "").toLowerCase().trim();
  return TARGET_ALIASES[raw] ?? raw;
}

// The proven prompt text (from the resume-package prototype) that tells the
// target agent to read RESUME.md and continue. `{md}`/`{json}` are swapped for
// the path form each agent expects (codex `cat`s files; cursor injects @refs).
// One instruction, three call sites (codex/cursor auto-launch args + the portable
// printed block). Kept short on purpose: RESUME.md's own 'READ ME FIRST' already
// carries the full contract (sidecar, stale-read caveats), so the prompt only has to
// get the agent to open the file — while keeping the one safety-critical rule (don't
// re-apply on-disk edits) inline. {md} is @-mentioned on the tools that support it
// (cursor/opencode/gemini) so the file is pulled straight into context.
const RESUME_INSTRUCTION =
  "Follow {md} to resume this coding session: read it IN FULL, then continue from its " +
  "'Resume Context' block. The edits it describes are ALREADY on disk — re-read the " +
  "current files, do not re-apply them.";

// Build the interactive launch command (so the user lands in a live session).
// The prototype's automated recall test used the one-shot `codex exec` / cursor
// `-p` forms; a user forking a session wants an interactive continuation.
function nonNativeLaunch(target: string, gitRoot: string): { cmd: string; args: string[] } {
  if (target === "codex") {
    const prompt = RESUME_INSTRUCTION.replace("{md}", "./.sailfish-resume/RESUME.md");
    return { cmd: "codex", args: ["-C", gitRoot, prompt] };
  }
  // cursor
  const prompt = RESUME_INSTRUCTION.replace("{md}", "@.sailfish-resume/RESUME.md");
  return { cmd: "cursor-agent", args: [prompt] };
}

// Roughly shell-quote an arg for a copy-pasteable printed command line.
function shellQuote(arg: string): string {
  return /[^A-Za-z0-9_./:@-]/.test(arg) ? `'${arg.replace(/'/g, "'\\''")}'` : arg;
}

// claude-code path (unchanged launch): write the native transcript into
// ~/.claude/projects/{encoded-git-root}/ and resume it. The orientation rides in
// as a trailing assistant turn so CC renders it and waits for the user's answer.
function resumeInClaudeCode(
  jsonlContent: string,
  sessionId: string,
  gitRoot: string,
  orientation: string
): void {
  const content = appendOrientationTurn(jsonlContent, orientation, gitRoot);

  const projectDirName = cwdToProjectDirName(gitRoot);
  const projectDir = join(homedir(), ".claude", "projects", projectDirName);
  const targetPath = join(projectDir, `${sessionId}.jsonl`);

  const confinedDir = join(homedir(), ".claude", "projects") + "/";
  if (!resolve(targetPath).startsWith(confinedDir)) {
    console.error("Refusing to write outside ~/.claude/projects/.");
    process.exit(1);
  }

  mkdirSync(projectDir, { recursive: true });
  writeFileSync(targetPath, content);
  console.log(`Conversation written to ${targetPath}`);

  if (process.stdin.isTTY) {
    launchInteractive("claude", ["--resume", sessionId], gitRoot, targetPath);
  } else {
    console.log(`\nsession_id=${sessionId}`);
    console.log(`cwd=${gitRoot}`);
  }
}

// Convert the CC transcript into the portable RESUME.md + resume.json package and
// write both into a dedicated, self-ignored subdir of the repo
// (<gitRoot>/.sailfish-resume/) rather than the repo root. This never clobbers a
// user's own RESUME.md, and a bundled `.gitignore` (`*`) keeps the whole dir out
// of `git add` — so it can't land in a commit (works even in repos with no
// pre-existing ignore rules). It stays INSIDE the repo (not /tmp) so
// cursor/windsurf @-mentions (`@.sailfish-resume/RESUME.md`) resolve against the
// workspace. NB: NOT `.sailfish/` — that's an existing git-tracked tooling dir
// (branch-metadata.json, spec-graph) with its own .gitignore/.gitattributes rules.
// Shared by the codex/cursor launch path and the windsurf/Devin manual path.
function writeNonNativePackage(
  jsonlContent: string,
  gitRoot: string,
  orientation: string
): { resumeMdPath: string; resumeJsonPath: string; resumeDir: string } {
  const session = extractPortableSession(jsonlContent);
  const pkg = renderNonNative(session, orientation);

  const resumeDir = join(gitRoot, ".sailfish-resume");
  const resumeMdPath = join(resumeDir, "RESUME.md");
  const resumeJsonPath = join(resumeDir, "resume.json");

  const confinedRoot = resolve(gitRoot) + "/";
  if (!resolve(resumeDir).startsWith(confinedRoot)) {
    console.error(`Refusing to write resume files outside ${gitRoot}.`);
    process.exit(1);
  }

  mkdirSync(resumeDir, { recursive: true });
  writeFileSync(join(resumeDir, ".gitignore"), "*\n"); // self-ignore → the dir is invisible to `git add`
  writeFileSync(resumeMdPath, pkg["RESUME.md"]);
  writeFileSync(resumeJsonPath, pkg["resume.json"]);
  console.log(`Resume package written to ${resumeDir}/ (git-ignored):\n  ${resumeMdPath}\n  ${resumeJsonPath}`);
  return { resumeMdPath, resumeJsonPath, resumeDir };
}

// codex / cursor path: convert the CC transcript to RESUME.md + resume.json in
// the git root, then launch the target agent (interactive when on a TTY).
function resumeInNonNative(
  target: string,
  jsonlContent: string,
  gitRoot: string,
  orientation: string
): void {
  const { resumeMdPath } = writeNonNativePackage(jsonlContent, gitRoot, orientation);
  // codex/cursor auto-launch, but the orientation lives in RESUME.md (not a
  // guaranteed opening turn), so echo it — with the manual headline.
  printOrientationBanner(orientation, true);

  const { cmd, args } = nonNativeLaunch(target, gitRoot);
  if (process.stdin.isTTY) {
    launchInteractive(cmd, args, gitRoot, resumeMdPath);
  } else {
    const printable = [cmd, ...args].map(shellQuote).join(" ");
    console.log(`\nresume_md=${resumeMdPath}`);
    console.log(`cwd=${gitRoot}`);
    console.log(`command: ${printable}`);
  }
}

// Write `content` to a uniquely-named file in the OS tmp dir; returns its path.
// Native emitters hand their store-import payloads to the target agent's CLI (which
// copies them into its own store), so these live in OS tmp — never the repo.
function writeTempFile(prefix: string, ext: string, content: string): string {
  const p = join(tmpdir(), `${prefix}-${process.pid}-${Date.now()}.${ext}`);
  // 0o600: the payload is the full customer transcript — don't leave it
  // world-readable on a shared /tmp between write and the post-import unlink.
  writeFileSync(p, content, { mode: 0o600 });
  return p;
}

// Report a failed CLI spawn (spawnSync import step) and exit. `binary` is the bare
// executable — named on ENOENT so the hint is actionable; `label` is the human
// operation (e.g. "opencode import") used for exit-code failures.
function reportSpawnFailure(binary: string, label: string, res: ReturnType<typeof spawnSync>): never {
  if (res.error) {
    const e = res.error as NodeJS.ErrnoException;
    if (e.code === "ENOENT") {
      console.error(`\n"${binary}" is not installed or not on your PATH.`);
      console.error(`Nothing was lost — install ${binary} and re-run this fork.`);
    } else {
      console.error(`${label} failed: ${e.message}`);
    }
  } else {
    const out = `${res.stdout || ""}\n${res.stderr || ""}`.trim();
    console.error(`${label} failed (exit ${res.status}):\n${out}`);
  }
  process.exit(1);
}

// Spawn an interactive agent (TTY path) with a uniform crash-guard: a missing
// binary (ENOENT) prints an install hint — naming the bare binary, plus the on-disk
// artifact when we have one so nothing is lost — instead of an uncaught stack
// trace. Any other spawn error is reported, never thrown.
function launchInteractive(cmd: string, args: string[], gitRoot: string, readyAt?: string): void {
  const printable = [cmd, ...args].map(shellQuote).join(" ");
  console.log(`\nLaunching: ${printable}\n`);
  const child = spawn(cmd, args, { cwd: gitRoot, stdio: "inherit" });
  child.on("error", (e: NodeJS.ErrnoException) => {
    if (e.code === "ENOENT") {
      console.error(`\n"${cmd}" is not installed or not on your PATH.`);
      console.error(
        readyAt
          ? `Your session is ready at ${readyAt} — nothing was lost. Install ${cmd}, then run:`
          : `Install ${cmd}, then run:`
      );
      console.error(`  ${printable}`);
    } else {
      console.error(`Failed to launch ${cmd}: ${e.message}`);
    }
    process.exit(1);
  });
  child.on("exit", (code) => process.exit(code ?? 0));
}

// Echo the worktree/branch orientation to stdout at launch. Native targets carry
// the interview as a trailing turn in the model's OWN history — but a large autofix
// session can exceed the target's context window and auto-compact on load (goose
// does this reliably), summarizing that turn away before the user ever sees it.
// Printing it here guarantees the worktree choice reaches the user regardless of
// what the model does with the transcript.
// `manual = true` for targets where the orientation lives only in RESUME.md (the
// agent must choose to raise it — codex/cursor/windsurf), vs the auto-greet targets
// whose orientation is an actual opening turn (opencode/goose/gemini).
function printOrientationBanner(orientation: string, manual = false): void {
  const bar = "─".repeat(64);
  console.log(`\n${bar}`);
  console.log(
    manual
      ? "Make sure the agent raises this with you before it changes any code:"
      : "The resumed agent opens by asking you this — answer it once the session loads:"
  );
  console.log(bar);
  console.log(orientation);
  console.log(bar);
}

// opencode path: convert the CC transcript to OpenCode's export JSON (orientation
// as the agent's own trailing assistant greeting), `opencode import` it (re-homes
// to gitRoot's project), then resume the imported session.
function resumeInOpenCode(jsonlContent: string, gitRoot: string, orientation: string): void {
  const session = extractPortableSession(jsonlContent);
  // Fresh nonce per fork: opencode `import` no-ops on an id collision, so a
  // deterministic id would resurrect a stale earlier import instead of landing
  // this one. A unique id makes every fork a clean resume session.
  const tmpFile = writeTempFile(
    "sailfish-fork-opencode",
    "json",
    JSON.stringify(renderOpenCode(session, orientation, randomUUID().slice(0, 8)))
  );

  const imp = spawnSync("opencode", ["import", tmpFile], { cwd: gitRoot, encoding: "utf-8" });
  try { unlinkSync(tmpFile); } catch { /* best-effort cleanup */ }
  if (imp.error || imp.status !== 0) reportSpawnFailure("opencode", "opencode import", imp);

  const out = `${imp.stdout || ""}\n${imp.stderr || ""}`;
  const sid = (out.match(/ses_[A-Za-z0-9]+/) || [])[0];
  if (!sid) {
    console.error(`opencode import did not report a session id. Output:\n${out.trim()}`);
    process.exit(1);
  }
  console.log(`Imported OpenCode session: ${sid}`);
  printOrientationBanner(orientation);

  // Resume interactively in the TUI (the default `opencode [project]` command
  // takes --session). `opencode run` is the HEADLESS form and requires a
  // message, so `opencode run -s <id>` errors with "must provide a message".
  const args = ["--session", sid];
  if (process.stdin.isTTY) {
    launchInteractive("opencode", args, gitRoot);
  } else {
    console.log(`\nsession_id=${sid}`);
    console.log(`cwd=${gitRoot}`);
    console.log(`command: opencode ${args.join(" ")}`);
  }
}

// goose path: feed the CC transcript to goose's first-party importer (with a
// deterministic ai-title → session name), then resume that named session. Goose
// consumes raw CC JSONL, so the orientation rides in as the same trailing
// assistant turn used for claude-code.
function resumeInGoose(jsonlContent: string, gitRoot: string, orientation: string): void {
  const oriented = appendOrientationTurn(jsonlContent, orientation, gitRoot);
  const session = extractPortableSession(oriented);
  const { content, sessionName } = prepareGooseImport(oriented, session);
  const tmpFile = writeTempFile("sailfish-fork-goose", "jsonl", content);

  const imp = spawnSync("goose", ["session", "import", tmpFile], { cwd: gitRoot, encoding: "utf-8" });
  try { unlinkSync(tmpFile); } catch { /* best-effort cleanup */ }
  if (imp.error || imp.status !== 0) reportSpawnFailure("goose", "goose session import", imp);
  console.log(`Imported Goose session: "${sessionName}"`);
  printOrientationBanner(orientation);

  // `goose session import` has no --name; the name comes from the ai-title, so
  // resume by that name.
  const args = ["session", "-r", "--name", sessionName];
  if (process.stdin.isTTY) {
    launchInteractive("goose", args, gitRoot);
  } else {
    const printable = ["goose", ...args].map(shellQuote).join(" ");
    console.log(`\nsession_name=${sessionName}`);
    console.log(`cwd=${gitRoot}`);
    console.log(`command: ${printable}`);
  }
}

// gemini path: gemini's resume is an in-TUI slash-command (`/chat resume <tag>`)
// with NO headless launch, so we can't spawn-into-resume. Write the checkpoint into
// ~/.gemini/tmp/<slug>/ (seeding the project markers, with the orientation as a
// trailing model turn) and print the manual steps.
function resumeInGemini(jsonlContent: string, gitRoot: string, orientation: string, issueId: number): void {
  const session = extractPortableSession(jsonlContent);
  const { tag, checkpointPath } = placeGeminiCheckpoint(
    gitRoot,
    renderGeminiCheckpoint(session, orientation),
    geminiResumeTag(issueId),
    [process.cwd()], // also seed under the dir fork was run from → `gemini` finds it even without cd-ing to the root
  );

  console.log(`Gemini checkpoint written:\n  ${checkpointPath}`);
  printOrientationBanner(orientation);
  console.log(
    `\nGemini resumes from inside its own TUI. Launch it from the repo root — run this\n` +
      `as one line (the cd matters: gemini finds the checkpoint by directory):\n` +
      `  cd ${gitRoot} && gemini\n` +
      `then inside gemini:\n` +
      `  /chat resume ${tag}`
  );
  if (!process.stdin.isTTY) {
    console.log(`\ncheckpoint=${checkpointPath}`);
    console.log(`cwd=${gitRoot}`);
    console.log(`resume_tag=${tag}`);
  }
}

// windsurf path: Windsurf is now Devin Desktop — a VS-Code-style GUI IDE that
// reads workspace files and supports @-mentions, with NO headless/CLI resume to
// spawn. So write the non-native RESUME.md + resume.json package into the repo
// (same confined write as codex/cursor, incl. the orientation "Branch & Worktree"
// section) and print the manual @-mention steps, mirroring the gemini manual
// target instead of launching an agent.
function resumeInWindsurf(jsonlContent: string, gitRoot: string, orientation: string): void {
  const { resumeMdPath } = writeNonNativePackage(jsonlContent, gitRoot, orientation);
  const prompt = RESUME_INSTRUCTION.replace("{md}", "@.sailfish-resume/RESUME.md");
  console.log(
    `\nContinue in Devin Desktop (the Windsurf app):\n` +
      `  1. Open ${gitRoot} in Devin Desktop\n` +
      `  2. Start a conversation and paste the prompt below\n` +
      `  3. it opens by asking how you want to handle the branch/worktree — answer that before it edits any code`
  );
  printCopyBlock(prompt, "Devin");
  if (!process.stdin.isTTY) {
    console.log(`\nresume_md=${resumeMdPath}`);
    console.log(`cwd=${gitRoot}`);
    console.log(`prompt=${prompt}`);
  }
}

// The framed copy-block shared by every manual paste target (opencode/gemini portable
// + windsurf/Devin): a plain caption OUTSIDE the frame, then the prompt fenced by two
// identical light (─) rules. It's the only framed block in the manual flow (the
// orientation stdout echo is gone from these paths), so "between the lines" reads as an
// unmistakable payload boundary without needing a heavier rule to stand apart.
function printCopyBlock(prompt: string, targetName: string): void {
  const fence = "─".repeat(64);
  console.log(`\nCopy everything between the lines and paste it into ${targetName}:`);
  console.log(fence);
  console.log(prompt);
  console.log(fence);
}

// Portable resume for a CLI agent whose TUI can't be launched with an initial
// prompt (opencode's TUI and gemini's TUI both take only a path). Write the
// .sailfish-resume/ package and print manual steps: open the agent in the repo,
// then hand it the resume prompt. This is the DEFAULT for opencode + gemini —
// it's version-INDEPENDENT (the agent's own LLM reads RESUME.md), unlike the
// native import/checkpoint paths (behind --native) that reverse-engineer the
// tool's private on-disk format and break on a version bump. No orientation echo on
// this manual path: RESUME.md's own 'Branch & Worktree' section carries it and the
// agent surfaces it in-context (where the user answers it) — step 3 is the heads-up.
function resumeInPortableCli(binary: string, jsonlContent: string, gitRoot: string, orientation: string): void {
  const { resumeMdPath } = writeNonNativePackage(jsonlContent, gitRoot, orientation);
  const prompt = RESUME_INSTRUCTION.replace("{md}", "@.sailfish-resume/RESUME.md");
  console.log(
    `\nContinue in ${binary} (portable resume — robust across ${binary} versions):\n` +
      `  1. cd ${gitRoot} && ${binary}\n` +
      `  2. paste the prompt below\n` +
      `  3. it opens by asking how you want to handle the branch/worktree — answer that before it edits any code`
  );
  printCopyBlock(prompt, binary);
  if (!process.stdin.isTTY) {
    console.log(`\nresume_md=${resumeMdPath}`);
    console.log(`cwd=${gitRoot}`);
    console.log(`launch=cd ${gitRoot} && ${binary}`);
    console.log(`prompt=${prompt}`);
  }
}

// The native-vs-portable policy in ONE place, so the dispatch and the --native
// validation can't diverge (and it's unit-testable without auth/I/O):
//  - claude-code, goose  → always native. Both ride on CC's OWN format (CC identity;
//    goose's first-party claude_code.rs importer), so native is robust here.
//  - opencode, gemini     → portable by default; --native opts into native. Their
//    native paths reverse-engineer a foreign, version-pinned format (opencode's
//    Effect-Schema export; gemini's /chat checkpoint) → fragile, so portable is the
//    safe default.
//  - codex, cursor, windsurf, copilot → always portable (no native path); --native is an error.
export function resumeMode(target: string, native: boolean): "native" | "portable" | "error" {
  if (target === "claude-code" || target === "goose") return "native";
  if (target === "opencode" || target === "gemini") return native ? "native" : "portable";
  return native ? "error" : "portable"; // codex / cursor / windsurf
}

// Single source of truth for which agents `fork` can resume into, and how. Adding
// a target = one entry here — there's no separate Set + dispatch cascade to keep in
// sync, so a half-wired target can't silently fall through to the codex/cursor path
// (it just isn't a key, and the unknown-agent check below rejects it).
interface ResumeContext {
  target: string;
  jsonlContent: string;
  sessionId: string;
  gitRoot: string;
  orientation: string;
  issueId: number;
  native: boolean; // --native: use the fragile native import/checkpoint path instead of portable
}
const RESUME_HANDLERS: Record<string, (c: ResumeContext) => void> = {
  "claude-code": (c) => resumeInClaudeCode(c.jsonlContent, c.sessionId, c.gitRoot, c.orientation),
  codex: (c) => resumeInNonNative(c.target, c.jsonlContent, c.gitRoot, c.orientation),
  cursor: (c) => resumeInNonNative(c.target, c.jsonlContent, c.gitRoot, c.orientation),
  // opencode/gemini: portable by default, native behind --native (see resumeMode).
  opencode: (c) =>
    resumeMode("opencode", c.native) === "native"
      ? resumeInOpenCode(c.jsonlContent, c.gitRoot, c.orientation)
      : resumeInPortableCli("opencode", c.jsonlContent, c.gitRoot, c.orientation),
  goose: (c) => resumeInGoose(c.jsonlContent, c.gitRoot, c.orientation),
  gemini: (c) =>
    resumeMode("gemini", c.native) === "native"
      ? resumeInGemini(c.jsonlContent, c.gitRoot, c.orientation, c.issueId)
      : resumeInPortableCli("gemini", c.jsonlContent, c.gitRoot, c.orientation),
  windsurf: (c) => resumeInWindsurf(c.jsonlContent, c.gitRoot, c.orientation),
  // GitHub Copilot (VS Code Copilot Chat / the Copilot CLI). Portable-only — it
  // reads the RESUME.md handoff; there's no native session format to import.
  copilot: (c) => resumeInPortableCli("copilot", c.jsonlContent, c.gitRoot, c.orientation),
};

export async function runFork(
  issueIdStr: string,
  opts: { cwd?: string; agent?: string; native?: boolean; token?: string; graphqlUrl?: string; companyId?: string }
): Promise<void> {
  // A --token lets the copied web-app command authenticate + resume in one shot
  // (mirrors `login`/`setup`). exchangeToken validates and persists the JWT, so
  // the existing readCredentials() path below then finds fresh, valid creds.
  if (opts.token) {
    try {
      await exchangeToken(opts.token, opts.graphqlUrl);
    } catch (err: any) {
      console.error(`Token exchange failed: ${err.message}`);
      process.exit(1);
    }
  }

  const creds = readCredentials();
  if (!creds) {
    console.error("Not authenticated. Run `npx @sailfish-ai/autofix login` first.");
    process.exit(1);
  }
  if (isExpired(creds)) {
    console.error("Credentials expired. Run `npx @sailfish-ai/autofix login` to re-authenticate.");
    process.exit(1);
  }

  if (!/^\d+$/.test(issueIdStr)) {
    console.error(`Invalid issue ID: "${issueIdStr}". Must be a number.`);
    process.exit(1);
  }
  const issueId = Number(issueIdStr);

  let gitRoot: string;
  try {
    gitRoot = getGitRoot(opts.cwd);
  } catch (err: any) {
    console.error(err.message);
    process.exit(1);
  }
  console.log(`Git root: ${gitRoot}`);

  const target = normalizeTarget(opts.agent);
  if (!(target in RESUME_HANDLERS)) {
    if (PLANNED_TARGETS.includes(target)) {
      console.error(
        `Resuming in "${target}" isn't available yet — coming soon.\n` +
          `For now, use --agent claude-code (the default), codex, cursor, opencode, gemini, goose, or windsurf.`
      );
    } else {
      const comingSoon = PLANNED_TARGETS.length ? `\nComing soon: ${PLANNED_TARGETS.join(", ")}.` : "";
      console.error(
        `Unknown agent "${opts.agent}". Supported: claude-code (aliases: cc, claude), codex, cursor, ` +
          `opencode (alias: oc), gemini (alias: gemini-cli), goose, windsurf (alias: devin), copilot.` +
          comingSoon
      );
    }
    process.exit(1);
  }

  // --native is meaningful only for targets with a native path (opencode/gemini).
  // On claude-code/goose it's a harmless no-op (they're native anyway); on
  // codex/cursor/windsurf there is no native path — error rather than silently ignore.
  if (resumeMode(target, !!opts.native) === "error") {
    console.error(
      `--native has no effect for "${target}": it has no native resume path and always uses the ` +
        `portable RESUME.md package. Drop --native.`
    );
    process.exit(1);
  }

  const client = new AutofixBackendClient(getCredentialsPath());

  // Internal cross-company resume: the web-app command passes the company that
  // owns the run so the backend resolves it there, not the caller's own company.
  const companyId = opts.companyId ? Number(opts.companyId) : undefined;

  try {
    console.log(`Looking up latest autofix run for issue ${issueId}...`);
    const runResult = await client.query<LatestRunResult>(GET_LATEST_AUTOFIX_RUN_BY_ISSUE_ID, {
      issueId,
      companyId,
    });

    const run = runResult.getLatestAutofixRunByIssueId;
    if (!run?.runId) {
      console.error(`No autofix run found for issue ${issueId}.`);
      console.error(`Autofix may not have run on this issue yet, or the run is too old to resume.`);
      process.exit(1);
    }
    const runId = run.runId;
    console.log(`Found run: ${runId}`);

    console.log("Downloading session log...");
    const result = await client.query<DownloadResult>(DOWNLOAD_ARTIFACT, {
      branch: "autofix",
      artifactId: runId,
      artifactType: "autofix_session_log",
      companyId,
    });

    const artifact = result.downloadArtifact;
    if (!artifact?.data) {
      console.error("No session log found for this run.");
      console.error("The run may still be in progress or too old — try again later, or fork a more recent issue.");
      process.exit(1);
    }

    let jsonlContent = Buffer.from(artifact.data, "base64").toString("utf-8");
    const sessionId = sanitizeSessionId(extractSessionId(jsonlContent) || runId);

    const originalCwd = extractCwd(jsonlContent);
    if (originalCwd && originalCwd !== gitRoot) {
      jsonlContent = patchCwd(jsonlContent, originalCwd, gitRoot);
      console.log(`Rewrote the session's saved file paths to your checkout: ${originalCwd} → ${gitRoot}`);
    }

    // Compute the worktree/branch orientation ONCE from the run's branch/PR/state
    // and thread it into whichever target resumes the session — every target
    // opens by offering the user how they want to work.
    const fixBranch = parseBranchFromUrl(run.branchUrl);
    if (fixBranch) console.log(`Fix branch: ${fixBranch}`);
    const orientation = buildOrientation({
      issueId,
      fixBranch,
      prUrl: run.prUrl,
      state: run.state,
      currentBranch: getCurrentBranch(gitRoot),
    });

    // Dispatch through the single handler table — no separate cascade to drift.
    RESUME_HANDLERS[target]({ target, jsonlContent, sessionId, gitRoot, orientation, issueId, native: !!opts.native });
  } catch (err: any) {
    if (err.message?.includes("Authentication failed")) {
      console.error("Authentication failed. Run `npx @sailfish-ai/autofix login` to re-authenticate.");
    } else if (err.message?.includes("timeout") || err.name === "AbortError") {
      console.error("Request timed out. Check your network connection and try again.");
    } else {
      console.error(`Error: ${err.message}`);
    }
    process.exit(1);
  }
}
